import { AxiosError } from "axios";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CUSTOM_FIELD_TYPE } from "src/api/contractGroup/contractgroup-type";
import {
  PartnerMemberCustomFieldModel,
  PartnerMemberInfo,
  PartnerMemberInputSaveItem,
} from "src/api/userAdditionalInfo/userAdditionalInfo-type";
import { BaseButton, BaseDatePicker, BaseInput, BaseSelect, ContentsTitle } from "src/components";
import { MultiSelectValue } from "src/components/multiSelect";
import GoToListButton from "src/components/GoToListButton";
import MetaTag from "src/components/layout/MetaTag";
import { useTabNavigation } from "src/hooks/useTabNavigation";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import { useErrorModal } from "src/recoil/errorModal/hook";
import { useModal } from "src/recoil/modalState/hook";
import { ViewYmdFormat, formatLargeNumberString, formatPhoneNumber } from "src/utils";
import { isUrl } from "src/utils/common-util";
import { useUserAdditionalInfo } from "../hooks/useUserAdditionalInfo";
import { CUSTOM_FIELD_TABS } from "../userAdditionalInfo-constant";

// 필드별 편집 상태 (텍스트류: inputValue / 선택류: optionIdList)
type InputEntry = { inputValue?: string; optionIdList?: number[] };

/**
 * 안내/설정 > 이용자 부가정보 > 상세 화면
 * - 회원정보 컨테이너(수정불가) + 이용자 부가정보 컨테이너(수정 모드에서 편집)
 * - 필드 정의(라벨/유형/옵션/순서)는 RCR11, 값은 RCR32 를 병합
 */
const UserAdditionalInfoDetail = () => {
  const { memberNo = "" } = useParams<{ memberNo: string }>();
  const navigate = useNavigate();
  const { setBaseModal } = useModal();
  const { openErrorModal } = useErrorModal();
  const { activeTab, clickTab } = useTabNavigation({ tabs: CUSTOM_FIELD_TABS });
  const { getCustomFieldList, getPartnerMemberDetail, savePartnerMemberDetail } = useUserAdditionalInfo();

  const [member, setMember] = useState<PartnerMemberInfo>();
  const [customFields, setCustomFields] = useState<PartnerMemberCustomFieldModel[]>([]);
  const [inputMap, setInputMap] = useState<Record<number, InputEntry>>({});
  const [savedInputMap, setSavedInputMap] = useState<Record<number, InputEntry>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  // 필드별 검증 에러 (fieldId → 메시지). 계약그룹(RHF mode:onSubmit)과 동일하게 저장 시도 후부터 노출한다
  const [errorMap, setErrorMap] = useState<Record<number, string>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  // 노출 필드만, orderNum 순
  const visibleFields = useMemo(
    () => customFields.filter((field) => field.isVisible && !field.isDeleted).sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0)),
    [customFields],
  );

  useEffect(() => {
    fetchDetail();
  }, [memberNo]);

  const fetchDetail = async () => {
    try {
      const [fields, extraInfo] = await Promise.all([getCustomFieldList(), getPartnerMemberDetail(memberNo)]);
      setCustomFields(fields || []);
      setMember(extraInfo?.member);

      // RCR32 inputList → inputMap
      const nextMap: Record<number, InputEntry> = {};
      (extraInfo?.inputList || []).forEach((input) => {
        if (input.optionList && input.optionList.length > 0) {
          nextMap[input.fieldId] = { optionIdList: input.optionList.map((option) => option.optionId) };
        } else {
          nextMap[input.fieldId] = { inputValue: input.inputValue || "" };
        }
      });
      setInputMap(nextMap);
      setSavedInputMap(nextMap);
      setErrorMap({});
      setIsSubmitted(false);
    } catch (error) {
      const errorResponse = error instanceof AxiosError ? error.response : undefined;
      const errorCode: string | undefined = errorResponse?.data?.meta?.errorCode;

      if (errorCode !== "eCT501") {
        openErrorModal({ statusCode: errorResponse?.status, errorCode, cause: error });
        return;
      }

      // eCT501: 미등록·탈퇴 회원
      setBaseModal({
        isOpen: true,
        title: "조회할 수 없는 회원입니다.",
        children: <span>미등록 또는 탈퇴한 회원입니다.</span>,
        btnRightTitle: "확인",
        onClick: () => {
          setBaseModal({ isOpen: false });
          navigate(PagePath.userAdditionalInfo.list);
        },
      });
    }
  };

  // 커스텀 필드 값 검증 (계약그룹 ContractGroupCustomFieldForm 과 동일한 규칙)
  // - 빈 값은 통과, URL 은 http:// 또는 https:// 로 시작하는 형식만 허용
  const validateEntry = (field: PartnerMemberCustomFieldModel, entry: InputEntry): string => {
    if (field.fieldType !== CUSTOM_FIELD_TYPE.URL) return "";

    const value = entry.inputValue?.trim();
    if (!value) return "";

    return isUrl(value) ? "" : "https:// 또는 http:// 로 시작하는 URL을 입력해주세요";
  };

  // 편집 상태 갱신 (저장 시도 이후에는 입력할 때마다 재검증 - RHF reValidateMode:onChange 와 동일)
  const updateEntry = (fieldId: number, entry: InputEntry) => {
    setInputMap((prev) => ({ ...prev, [fieldId]: entry }));

    if (!isSubmitted) return;
    const field = visibleFields.find((item) => item.fieldId === fieldId);
    setErrorMap((prev) => ({ ...prev, [fieldId]: field ? validateEntry(field, entry) : "" }));
  };

  // 뷰 모드 값 포맷팅
  const renderFieldValue = (field: PartnerMemberCustomFieldModel) => {
    const entry = inputMap[field.fieldId];
    if (!entry) return "-";

    switch (field.fieldType) {
      case CUSTOM_FIELD_TYPE.RADIO:
      case CUSTOM_FIELD_TYPE.CHECKBOX: {
        const names = (entry.optionIdList || [])
          .map((optionId) => field.optionList?.find((option) => option.optionId === optionId)?.optionName)
          .filter(Boolean);
        return names.length > 0 ? names.join(", ") : "-";
      }
      case CUSTOM_FIELD_TYPE.NUMBER:
        return entry.inputValue ? formatLargeNumberString(entry.inputValue) || "-" : "-";
      case CUSTOM_FIELD_TYPE.CALENDAR:
        return entry.inputValue && moment(entry.inputValue).isValid() ? moment(entry.inputValue).format(ViewYmdFormat.YYYY_MM_DD) : "-";
      case CUSTOM_FIELD_TYPE.URL: {
        const url = entry.inputValue?.trim();
        if (!url) return "-";
        // http:// 또는 https:// 로 시작하는 경우에만 링크로 노출
        if (/^https?:\/\//i.test(url)) {
          return (
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-underline text-blue">
              {url}
            </a>
          );
        }
        return url;
      }
      default:
        return entry.inputValue || "-";
    }
  };

  // 수정 모드 입력 위젯
  const renderFieldInput = (field: PartnerMemberCustomFieldModel) => {
    const entry = inputMap[field.fieldId] || {};
    const optionOptions = field.optionList?.map((option) => ({ value: String(option.optionId), label: option.optionName })) || [];

    switch (field.fieldType) {
      case CUSTOM_FIELD_TYPE.RADIO:
        return (
          <BaseSelect
            className="minmax240"
            placeholder="선택해주세요"
            stateOptions={optionOptions}
            value={entry.optionIdList?.[0] !== undefined ? String(entry.optionIdList[0]) : ""}
            setStateValue={(value: string) => updateEntry(field.fieldId, { optionIdList: value ? [Number(value)] : [] })}
          />
        );
      case CUSTOM_FIELD_TYPE.CHECKBOX:
        return (
          <div className="minmax240">
            <MultiSelectValue
              options={optionOptions}
              value={(entry.optionIdList || []).map(String)}
              onChange={(values) => updateEntry(field.fieldId, { optionIdList: values.map(Number) })}
              placeholder="선택해주세요"
            />
          </div>
        );
      case CUSTOM_FIELD_TYPE.NUMBER:
        return (
          <BaseInput
            type="number"
            className="minmax240"
            placeholder="입력해주세요"
            value={entry.inputValue || ""}
            onChange={(value: string) => updateEntry(field.fieldId, { inputValue: value })}
          />
        );
      case CUSTOM_FIELD_TYPE.CALENDAR:
        return (
          <BaseDatePicker
            className="minmax240"
            type="date"
            selectedDate={entry.inputValue && moment(entry.inputValue).isValid() ? moment(entry.inputValue).toDate() : undefined}
            // 계약그룹과 동일: moment(date) 를 UTC ISO(2026-08-19T15:00:00.000Z)로 저장 (moment.toJSON = toISOString)
            setDate={(date: any) => updateEntry(field.fieldId, { inputValue: date ? moment(date).toISOString() : "" })}
          />
        );
      default:
        // TEXT / URL
        return (
          <BaseInput
            type="text"
            className="minmax240"
            placeholder="입력해주세요"
            value={entry.inputValue || ""}
            onChange={(value: string) => updateEntry(field.fieldId, { inputValue: value })}
            errorText={errorMap[field.fieldId]}
          />
        );
    }
  };

  // 저장
  const onClickSave = () => {
    // 검증 실패 필드가 하나라도 있으면 저장 모달을 띄우지 않고 에러 텍스트만 노출
    setIsSubmitted(true);
    const nextErrorMap: Record<number, string> = {};
    visibleFields.forEach((field) => {
      const message = validateEntry(field, inputMap[field.fieldId] || {});
      if (message) nextErrorMap[field.fieldId] = message;
    });
    setErrorMap(nextErrorMap);
    if (Object.keys(nextErrorMap).length > 0) return;

    const buildInputList = (): PartnerMemberInputSaveItem[] => {
      return visibleFields.map((field) => {
        const entry = inputMap[field.fieldId] || {};
        if (field.fieldType === CUSTOM_FIELD_TYPE.RADIO || field.fieldType === CUSTOM_FIELD_TYPE.CHECKBOX) {
          return { fieldId: field.fieldId, optionList: (entry.optionIdList || []).map((optionId) => ({ optionId })) };
        }
        return { fieldId: field.fieldId, inputValue: entry.inputValue ?? "" };
      });
    };

    const execute = async () => {
      try {
        await savePartnerMemberDetail(memberNo, buildInputList());
      } finally {
        setBaseModal({ isOpen: false });
      }

      // 실패 시 공통 에러 모달은 useApiOperation 에서 노출 - 위에서 던지므로 편집 모드가 유지돼 입력값이 보존된다
      await fetchDetail();
      setIsEditMode(false);
      setBaseModal({
        isOpen: true,
        title: "저장되었습니다.",
        btnRightTitle: "확인",
        onClick: () => setBaseModal({ isOpen: false }),
      });
    };

    setBaseModal({
      isOpen: true,
      title: "저장하시겠습니까?",
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => execute(),
      onClose: () => setBaseModal({ isOpen: false }),
    });
  };

  // 수정취소
  const onClickCancelEdit = () => {
    setInputMap(savedInputMap);
    setErrorMap({});
    setIsSubmitted(false);
    setIsEditMode(false);
  };

  const memberRows: { label: string; value?: string; verified?: boolean }[] = [
    { label: "회원번호", value: member?.memberNo },
    { label: "휴대폰 번호", value: member?.phoneNumber ? formatPhoneNumber(member.phoneNumber) : "", verified: member?.isPhoneNumberVerified },
    { label: "닉네임", value: member?.nickname },
    { label: "닉네임(추가)", value: member?.secondaryNickname },
    { label: "이메일", value: member?.email, verified: member?.isEmailVerified },
  ];

  return (
    <div>
      <MetaTag subTitle={"이용자 부가정보"} />
      <ContentsTitle title="" tabs={CUSTOM_FIELD_TABS} activeTab={activeTab} clickTab={clickTab} />
      <div className="contents-container__scroll">
        <div className="contents-container__wrap">
          {/* 회원정보 (수정불가) */}
          <article className="contents-container__wrap-article">
            <div className="contents-container__sub-title">
              <h2>회원정보</h2>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
              {memberRows.map((row) => (
                <section className="contents-container__grid" key={row.label}>
                  <div className="contents-container__grid-index">
                    <p>{row.label}</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div>
                      <div className="flex-center-start">
                        <span>{row.value || "-"}</span>
                        {row.verified && <div className="ml4 ic_certificate shrink-0"></div>}
                      </div>
                    </div>
                  </div>
                </section>
              ))}
            </div>
          </article>

          {/* 커스텀 필드 */}
          <article className="contents-container__wrap-article">
            <div className="contents-container__sub-title">
              <h2>커스텀 필드</h2>
            </div>
            {visibleFields.length === 0 ? (
              <div className="contents-container__grid-contents">
                <div className="flex-row flex-center-start px20" style={{ backgroundColor: "#f8f8fa", borderRadius: "10px", height: 80 }}>
                  <span>이용자 부가정보를 커스텀하여 관리할 수 있습니다.</span>
                  <BaseButton
                    className="color-white ml10"
                    title="커스텀필드 관리"
                    onClick={() => navigate(PagePath.userAdditionalInfo.customField)}
                  />
                </div>
              </div>
            ) : (
              visibleFields.map((field) => (
                <section className="contents-container__grid" key={field.fieldId}>
                  <div className="contents-container__grid-index">
                    <p>{field.fieldName}</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    {isEditMode ? renderFieldInput(field) : <div><span>{renderFieldValue(field)}</span></div>}
                  </div>
                </section>
              ))
            )}
          </article>
        </div>
      </div>

      <div className="contents-container__btn-wrap">
        <div className="left-area">
          <GoToListButton />
        </div>
        <div className="right-area">
          {isEditMode ? (
            <>
              <BaseButton className="color-white size-large mr10" title="수정취소" onClick={onClickCancelEdit} />
              <BaseButton className="size-large" title="저장" onClick={onClickSave} />
            </>
          ) : (
            <BaseButton className="size-large" title="수정" onClick={() => setIsEditMode(true)} />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAdditionalInfoDetail;
